/**
 * **D339 — the store-delivered app keeps its saved sign-in in the platform keystore, not in WebKit.**
 *
 * `credentials.ts` names the durable session row — refresh token, binding, session id, family id —
 * and on the browser PWA it lives in origin-scoped IndexedDB, as `device-cache.md` says. In the
 * container that put the one record a resident cannot rebuild without an SMS inside the storage
 * WebKit serves from its Networking process: the process iOS kills under memory pressure (D309),
 * and whose boot read rejected on 2026-09-25 and put a resident on the OTP screen over an intact
 * session (`STATE.md` §5). The boot retry loop narrows that; this module removes the class for the
 * container by moving the row to where `device-cache.md`'s native row always said it belonged —
 * **Keychain on iOS, an Android-Keystore-wrapped app-private file on Android** — through one
 * in-house plugin, `SeloGateSession`.
 *
 * **It is a `KeyValueStore`-shaped adapter for the `credentials` collection only**, so
 * `readCredentials` / `writeCredentials` / `clearCredentials` keep their signatures and the six clear
 * paths keep reaching the one function they always reached. Everything else in the device cache —
 * the snapshot, the profile, the outbox, D152's refresh marker — stays in IndexedDB. The browser PWA
 * never constructs one of these.
 *
 * Six rules, each the reason a line below exists.
 *
 * 1. **A read that FAILS rejects; only a read that succeeds with nothing is signed out** — the
 *    e96858c4 rule, carried across the move. A keystore that is locked before first unlock, a
 *    Keystore exception, a bridge that never answers, an answer this module cannot read: each is a
 *    {@link NativeCredentialStoreError}, which `SessionManager`'s restore loop retries. Nothing here
 *    ever turns a failure into `undefined`. **One code is not transient, and the native side ends it
 *    rather than this one**: `unreadable` is a record that can never be opened (a ciphertext whose
 *    tag fails or whose key is gone). The plugin drops that record before rejecting, so the retry
 *    250 ms later reads an adopted, empty store and signs out — instead of spending the whole budget
 *    on every launch — and the sink still sees `NativeCredential-unreadable` on both reports.
 * 2. **The move is one-way and loses nothing.** The native side reports whether this install has
 *    *adopted* the keystore. Until it has, the IndexedDB row is read, written to the keystore, and
 *    removed from IndexedDB **only after that write resolved** — a failed write leaves the row where
 *    it was for the next launch. A legacy store that degraded to memory cannot answer *no row*
 *    honestly, so that is a failed read too ({@link LegacyCredentialsUnreadableError}).
 * 3. **An un-adopted read's value is never trusted.** On iOS a Keychain item survives app deletion;
 *    the native side clears it on the first launch of a fresh install, and this side ignores any
 *    value an un-adopted read carries, so a reinstall signs out exactly as it did in IndexedDB.
 *    **And on a fresh install the IndexedDB row is dropped, never carried** (`freshInstall`, Android):
 *    Auto Backup restores the WebView's IndexedDB on a reinstall as it was when the backup ran, so
 *    the row is a rotation or more stale, and carrying it presents a retired family id — the server
 *    revokes the session and pages `RefreshFamilyMismatch` (production, 2026-09-25, `STATE.md` §5).
 *    The carry exists for the first launch after an UPGRADE, and that is the only case it serves. iOS
 *    does not report the field: an App Store reinstall restores no app data, so there is no row.
 *    🔸 **One iOS case is NOT covered and it is stated rather than guessed at**: a device restored
 *    from an iCloud or encrypted backup taken BEFORE D339 brings back IndexedDB without the adopted
 *    flag, and the row is carried exactly as an upgrade's would be — a retired family id if the
 *    backup predates a refresh (revoke + `RefreshFamilyMismatch`), a cloned session if not. iOS has
 *    no `firstInstallTime`; every substitute (container vs bundle dates) is a heuristic whose false
 *    *fresh* signs an upgrading resident out, so none is used. This is not a regression — pre-D339
 *    the same restored row was read directly — and it closes by itself: a backup taken after D339
 *    carries the flag, and the flag with no `ThisDeviceOnly` item reads as signed out.
 * 4. **Reached through Capacitor's injected global, never an import** (D166) — `web/` does not
 *    depend on `@capacitor/core`, and `nativePromise` rather than `Capacitor.Plugins` because those
 *    proxies are built at import time by a registration this bundle never makes.
 * 5. **Every call is bounded.** An unregistered plugin on iOS never calls back at all
 *    (`CapacitorBridge.handleJSCall` logs and returns), so an unbounded read would hang the boot
 *    where the pre-D339 read at least answered. A timeout is a failure, and rule 1 retries it.
 * 6. **Nothing here logs, and the error name carries a code, never a value.** The options carry the
 *    refresh token and the binding; the sink reports `errorName`, so the name is built from the
 *    native side's short code and nothing else.
 */

import type { KeyValueStore, StoreName } from '../device-cache/store'
import { isNativeContainer } from '../pwa/nativeContainer'
import { TIMED_OUT, deadline } from './timeout'

/** The plugin's JS name. `SeloGateSessionPlugin` on both platforms answers to exactly this. */
export const NATIVE_SESSION_PLUGIN = 'SeloGateSession'

/**
 * 🔸 **Assumption, not a decision — how long one keystore call may take before it counts as failed.**
 * A Keychain or Keystore round trip is milliseconds; the number exists for the call that never
 * answers (rule 5). A false timeout on a read costs one retry of the restore loop; on a write it is
 * the same fail-closed answer a refused write gets (D128). Unmeasured on a device.
 */
export const NATIVE_CREDENTIAL_CALL_TIMEOUT_MS = 3_000

/**
 * A keystore call that failed, timed out, or answered in a shape this module does not know.
 *
 * **Its `name` is what `credential-read-failed` reports**, so it is built from the native side's
 * short code — `locked`, `keychain`, `keystore`, `timeout` — and must stay inside the sink's
 * `DIAGNOSTIC_TOKEN` pattern. It is deliberately not a `DOMException` name: `storeRecovery.ts`'s
 * ladder acts only on those, and a keystore failure is no reason to destroy IndexedDB.
 */
export class NativeCredentialStoreError extends Error {
  constructor(readonly code: string) {
    super(`native-credential-store: ${code}`)
    this.name = `NativeCredential-${code}`
  }
}

/**
 * The first native read of this install found no adopted keystore, and the IndexedDB store it must
 * carry the row over from is a memory store — which answers every read with nothing. Reading that
 * as *no row* would mark the keystore adopted and strand an intact row for good, which is the
 * 2026-09-25 defect arriving through the migration. So it is a failed read, and the restore loop
 * re-reads through a fresh connection.
 */
export class LegacyCredentialsUnreadableError extends Error {
  constructor() {
    super('legacy-credentials-unreadable')
    this.name = 'LegacyStoreNotDurable'
  }
}

interface CapacitorBridge {
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

/**
 * **Decided by the container alone, never by whether the bridge answered.** A container whose
 * bridge has not injected `nativePromise` still uses the keystore — the call fails and is retried —
 * because falling back to IndexedDB there would split one resident's session across two stores.
 */
export function usesNativeCredentialStore(): boolean {
  return isNativeContainer()
}

const CODE = /^[a-z0-9-]{1,24}$/

function codeOf(error: unknown): string {
  const code = (error as { code?: unknown } | null | undefined)?.code
  return typeof code === 'string' && CODE.test(code) ? code : 'unknown'
}

async function call(method: 'read' | 'write' | 'clear', options: Record<string, string>): Promise<unknown> {
  const capacitor = (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
  if (typeof capacitor?.nativePromise !== 'function') throw new NativeCredentialStoreError('no-bridge')
  const budget = deadline(NATIVE_CREDENTIAL_CALL_TIMEOUT_MS)
  try {
    const answer = await budget.settle(capacitor.nativePromise(NATIVE_SESSION_PLUGIN, method, options))
    if (answer === TIMED_OUT) throw new NativeCredentialStoreError('timeout')
    return answer
  } catch (error) {
    if (error instanceof NativeCredentialStoreError) throw error
    throw new NativeCredentialStoreError(codeOf(error))
  } finally {
    budget.cancel()
  }
}

interface NativeRead {
  /** The stored record's JSON, when there is one. */
  readonly value: string | undefined
  /** Whether this install has adopted the keystore — the migration and fresh-install marker. */
  readonly adopted: boolean
  /** Rule 3: an un-adopted read on a fresh install, whose IndexedDB row is a backup restore. */
  readonly freshInstall: boolean
}

/** Rule 1: an answer this module cannot read is a failure, never an empty store. */
async function nativeRead(stage: string): Promise<NativeRead> {
  const answer = (await call('read', { stage })) as
    { value?: unknown; adopted?: unknown; freshInstall?: unknown } | null | undefined
  const value = answer?.value
  const freshInstall = answer?.freshInstall
  if (
    typeof answer?.adopted !== 'boolean' ||
    (value !== null && value !== undefined && typeof value !== 'string') ||
    (freshInstall !== undefined && typeof freshInstall !== 'boolean')
  ) {
    throw new NativeCredentialStoreError('malformed')
  }
  return {
    value: typeof value === 'string' ? value : undefined,
    adopted: answer.adopted,
    freshInstall: freshInstall === true,
  }
}

/**
 * One adapter per legacy store, so what an adapter learns about adoption lives on the adapter rather
 * than in module state. A reopened store gets a fresh adapter, which costs at most one redundant
 * IndexedDB remove.
 */
let adapters = new WeakMap<KeyValueStore, NativeCredentialStore>()

/** Test hook: forget every adapter, and with them what this document learned about adoption. */
export function resetNativeCredentialsForTests(): void {
  adapters = new WeakMap()
}

/**
 * A stored value that does not parse is a row that was half-written, and `readCredentials`'s own rule
 * for that is *treat it as no row and remove it* — so it is handed back as an empty record, which
 * fails that function's field checks and reaches {@link NativeCredentialStore.remove}. It is a read
 * that SUCCEEDED; rule 1 is about reads that did not.
 */
function parse<T>(value: string | undefined): T | undefined {
  if (value === undefined) return undefined
  try {
    return JSON.parse(value) as T
  } catch {
    return {} as T
  }
}

function onlyCredentials(store: StoreName): void {
  if (store !== 'credentials') throw new Error(`native credential store holds credentials only, not ${store}`)
}

class NativeCredentialStore implements KeyValueStore {
  readonly durable = true

  /**
   * Stages this adapter has seen adopted. It only saves a redundant IndexedDB remove; the native
   * side's own marker is what the migration trusts.
   */
  private readonly adoptedStages = new Set<string>()

  constructor(
    /** The IndexedDB store the row is carried over from, once (rule 2). */
    private readonly legacy: KeyValueStore,
  ) {}

  async get<T>(store: StoreName, stage: string): Promise<T | undefined> {
    onlyCredentials(store)
    const read = await nativeRead(stage)
    if (read.adopted) {
      if (!this.adoptedStages.has(stage)) {
        this.adoptedStages.add(stage)
        // A carry whose keystore write resolved late (after its deadline) left the IndexedDB copy
        // behind, and nothing else would ever remove it. Not awaited: a dead connection holds a
        // transaction for its full deadline, and this read must not wait on a cleanup.
        void this.legacy.remove('credentials', stage).catch(() => undefined)
      }
      return parse<T>(read.value)
    }
    // Rule 3: an un-adopted read is a fresh install or the first launch after the upgrade, and its
    // value is ignored whatever it says.
    if (read.freshInstall) {
      // A fresh install signs out: whatever IndexedDB holds came back with a backup restore, and
      // presenting it is a revoke and a page. Adopt, and take the restored row with it.
      await call('clear', { stage })
      this.adoptedStages.add(stage)
      await this.legacy.remove('credentials', stage).catch(() => undefined)
      return undefined
    }
    // Rule 2 from here: the first launch after the upgrade — carry the IndexedDB row over, once.
    if (!this.legacy.durable) throw new LegacyCredentialsUnreadableError()
    const row = await this.legacy.get<T>('credentials', stage)
    if (row === undefined) {
      // Nothing to carry: record that this install has adopted the keystore, so no later read asks
      // IndexedDB again. `clear` is that record on the native side.
      await call('clear', { stage })
      this.adoptedStages.add(stage)
      return undefined
    }
    await call('write', { stage, value: JSON.stringify(row) })
    this.adoptedStages.add(stage)
    // **Only now**, with the keystore holding it. A failed remove leaves a copy that nothing reads
    // again — the install is adopted — and the next clear removes it.
    await this.legacy.remove('credentials', stage).catch(() => undefined)
    return row
  }

  async put(store: StoreName, stage: string, value: unknown): Promise<void> {
    onlyCredentials(store)
    try {
      await call('write', { stage, value: JSON.stringify(value) })
    } catch (error) {
      // **A write that ran out its deadline may still commit** — the native call is not cancelled,
      // only abandoned — and every caller treats this rejection as *nothing was stored* (a sign-in
      // that failed, a rotation that failed closed). So the keystore is made to agree: a clear is
      // queued behind the write on the plugin's serial queue, best-effort. A refused write needs
      // none, having stored nothing.
      if (error instanceof NativeCredentialStoreError && error.code === 'timeout') {
        await call('clear', { stage }).catch(() => undefined)
      }
      throw error
    }
    if (!this.adoptedStages.has(stage)) {
      // A sign-in that follows a boot whose read never landed would otherwise leave the older row in
      // IndexedDB beside the new one in the keystore.
      this.adoptedStages.add(stage)
      await this.legacy.remove('credentials', stage).catch(() => undefined)
    }
  }

  /**
   * **Every session end reaches this**, through `clearCredentials`. It is tried twice, because a
   * clear that fails leaves a session a signed-out resident believes is gone. A second failure
   * throws, and **`endSession`'s fallback cannot finish it** — `destroy()` takes IndexedDB and never
   * the keystore — so `clearCredentials` records the clear as pending and the next read completes
   * it before answering. The IndexedDB copy goes too, best-effort, in case a migration's remove
   * failed.
   */
  async remove(store: StoreName, stage: string): Promise<void> {
    onlyCredentials(store)
    try {
      await call('clear', { stage })
    } catch {
      await call('clear', { stage })
    }
    this.adoptedStages.add(stage)
    await this.legacy.remove('credentials', stage).catch(() => undefined)
  }

  clear(): Promise<void> {
    return Promise.reject(new Error('native credential store: clear is not supported'))
  }

  keys(): Promise<readonly string[]> {
    return Promise.reject(new Error('native credential store: keys is not supported'))
  }

  destroy(): Promise<void> {
    return Promise.reject(new Error('native credential store: destroy is not supported'))
  }
}

/**
 * The store the credential row is read from and written to: the keystore adapter in the container,
 * and `store` itself everywhere else — which is the whole of what keeps the browser PWA untouched.
 */
export function credentialStoreFor(store: KeyValueStore): KeyValueStore {
  if (!usesNativeCredentialStore()) return store
  let adapter = adapters.get(store)
  if (adapter === undefined) {
    adapter = new NativeCredentialStore(store)
    adapters.set(store, adapter)
  }
  return adapter
}
